import {FamilyTreeNodeStyle, FamilyTreeType} from "../models/FamilyTreeType";
import {LifeStatus, MemberType, sortMemberTypeByAgeDescending} from "../models/MemberType";
import {FamilyRelationshipType} from "../models/FamilyRelationshipType";
import {Edge, Node} from "@xyflow/react";
import {findExtendedParents, findPartners, isLegalPartnerRelationship} from "../FamilyRelationshipService";
import {FamilyRelationship} from "../models/FamilyRelationship";
import {FamilyMode} from "./AddFamilyMember/FamilyMode";
import {
    FAMILY_MEMBER_HEIGHT,
    HORIZONTAL_EDGE_OFFSET,
    HORIZONTAL_NODE_GAP,
    NODE_WIDTH_DEFAULT,
    OTHER_MEMBERS_MARGIN_TOP,
    OTHER_MEMBERS_SECTION_HEIGHT_EMPTY,
    OTHER_MEMBERS_SECTION_HEIGHT_WITH_MEMBERS,
    VERTICAL_NODE_GAP
} from "./FamilyTreeLayoutConstants";
import {buildLevelMap} from "./FamilyTreeLevelUtils";
import {getCenterXFromNodes} from "./FamilyTreeLayoutMath";
import {
    centerExtendedFamilyAboveMain,
    enforceExtendedFamilySides
} from "./FamilyTreeAlignmentLayout";
import {enforcePartnerMaxGap} from "./FamilyTreePartnerLayout";
import {getNodeDimensions, getNodeWidth} from "./FamilyTreeNodeSizing";
import {applyMainTreeTidyLayout} from "./FamilyTreeTidyLayout";
import {applyCenteredTidySpacing} from "./FamilyTreeTidySpacingLayout";
import {getNodeHandleX, getNodeHandleY, isOtherMembersSection} from "./FamilyTreeNodeUtils";

// These pixel offsets stagger single-parent child edges when both sides have
// non-joint children. The partner-side drop is smaller because that edge starts
// farther right, so it needs less vertical travel to stay visually separated.
const SINGLE_PARENT_MEMBER_VERTICAL_DROP = 34;
const SINGLE_PARENT_PARTNER_VERTICAL_DROP = 26;

function getChildRelationships(member?: MemberType) {
    return member?.family?.filter(rel => rel.type === FamilyRelationshipType.CHILD && rel.fromMember) ?? [];
}

function getBaseNodeStyle(member: MemberType): FamilyTreeNodeStyle {
    return member.lifeStatus === LifeStatus.Deceased
        ? FamilyTreeNodeStyle.DECEASED
        : FamilyTreeNodeStyle.DEFAULT;
}

function addFamilyMemberNode(
    member: MemberType,
    nodes: Node[],
    onClick?: (member: MemberType, familyMode: FamilyMode) => void,
    familyMode: FamilyMode = FamilyMode.IMMEDIATE,
    x = 0,
    y = 0
) {
    if (!nodes.some(n => n.id === member.id)) {
        const {width, height} = getNodeDimensions(member);
        nodes.push({
            id: member.id,
            type: "familyMember",
            data: {
                firstName: member.firstName,
                middleInitial: member.middleInitial ?? undefined,
                lastName: member.lastName,
                age: member.age,
                state: member.stateAbbr ?? undefined,
                onClick: onClick ? () => onClick(member, familyMode) : undefined,
                style: getBaseNodeStyle(member),
            },
            position: {x, y},
            width,
            height
        });
    }
}

function applyPrimaryAndPartnerStyles(nodes: Node[], primaryContact: MemberType) {
    const primaryId = primaryContact.id;
    const legalPartnerIds = (primaryContact.family ?? [])
        .filter(rel => isLegalPartnerRelationship(rel.type))
        .map(rel => rel.fromMember?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);

    nodes.forEach(node => {
        if (node.type !== 'familyMember') return;
        const nodeData = node.data;
        if (!nodeData) return;

        const existingStyle = nodeData.style as FamilyTreeNodeStyle | undefined;
        if (existingStyle === FamilyTreeNodeStyle.DECEASED) return;
        let style = existingStyle ?? FamilyTreeNodeStyle.DEFAULT;
        if (node.id === primaryId) {
            style = FamilyTreeNodeStyle.PRIMARY_CONTACT;
        } else if (legalPartnerIds.includes(node.id)) {
            style = FamilyTreeNodeStyle.PRIMARY_LEGAL_PARTNER;
        }

        node.data = {
            ...nodeData,
            style,
        };
    });
}

type RelationshipLike = {
    fromMember?: MemberType;
    type?: FamilyRelationshipType | string;
};

function collectExSpouseIds(root: MemberType): string[] {
    const visited = new Set<string>();
    const exSpouseIds = new Set<string>();
    const stack: MemberType[] = [root];

    while (stack.length > 0) {
        const current = stack.pop();
        if (!current?.id || visited.has(current.id)) continue;
        visited.add(current.id);

        const familyRels = (current.family ?? []) as RelationshipLike[];
        const otherMemberRels = (Array.isArray(current.otherMembers)
            ? (current.otherMembers as RelationshipLike[])
            : []);

        [...familyRels, ...otherMemberRels].forEach(rel => {
            const related = rel.fromMember;
            if (rel.type === FamilyRelationshipType.EX_SPOUSE && related?.id) {
                exSpouseIds.add(related.id);
            }
            if (related?.id && !visited.has(related.id)) {
                stack.push(related);
            }
        });
    }

    return Array.from(exSpouseIds);
}

function deepFreeze<T>(value: T): T {
    if (!value || typeof value !== 'object') return value;
    Object.getOwnPropertyNames(value).forEach(name => {
        const child = (value as Record<string, unknown>)[name];
        if (child && typeof child === 'object') {
            deepFreeze(child);
        }
    });
    return Object.freeze(value);
}

function freezeNodeData(nodes: Node[]) {
    if (process.env.NODE_ENV === 'production') return;
    nodes.forEach(node => {
        if (node.data && typeof node.data === 'object' && !Object.isFrozen(node.data)) {
            deepFreeze(node.data);
        }
    });
}

function applyExSpouseStyles(nodes: Node[], primaryContact: MemberType) {
    const exSpouseIds = collectExSpouseIds(primaryContact);

    exSpouseIds.forEach(exId => {
        const node = nodes.find(n => n.type === 'familyMember' && n.id === exId);
        if (!node?.data) return;
        const nodeData = node.data;
        const existingStyle = nodeData.style as FamilyTreeNodeStyle | undefined;
        if (existingStyle === FamilyTreeNodeStyle.DECEASED) return;

        node.data = {
            ...nodeData,
            style: FamilyTreeNodeStyle.DOTTED,
            relationship: "Ex-Spouse",
        };
    });
}

function getUniqueSortedChildren(member: MemberType, partner?: MemberType): MemberType[] {
    const memberChildrenRels = getChildRelationships(member).filter(rel => rel.fromMember.id);
    const partnerChildrenRels = getChildRelationships(partner).filter(rel => rel.fromMember.id);
    const memberChildIds = new Set(memberChildrenRels.map(rel => rel.fromMember.id));
    const partnerChildIds = new Set(partnerChildrenRels.map(rel => rel.fromMember.id));

    // Helper to group with partner if exists
    function groupWithPartner(child: MemberType): MemberType[] {
        const childPartner = child.family?.find(rel => rel.type !== FamilyRelationshipType.CHILD && rel.type !== FamilyRelationshipType.PARENT)?.fromMember;
        return childPartner ? [child, childPartner] : [child];
    }

    // Helper for each group
    function getSingleMemberChildren(): MemberType[] {
        return memberChildrenRels
            .filter(rel => !partnerChildIds.has(rel.fromMember.id))
            .map(rel => rel.fromMember);
    }

    function getJointChildren(): MemberType[] {
        return memberChildrenRels
            .filter(rel => partnerChildIds.has(rel.fromMember.id))
            .map(rel => rel.fromMember);
    }

    function getSinglePartnerChildren(): MemberType[] {
        return partnerChildrenRels
            .filter(rel => !memberChildIds.has(rel.fromMember.id))
            .map(rel => rel.fromMember);
    }

    const orderedChildren: MemberType[] = [];
    sortMemberTypeByAgeDescending(getSingleMemberChildren()).forEach(child => orderedChildren.push(...groupWithPartner(child)));
    sortMemberTypeByAgeDescending(getJointChildren()).forEach(child => orderedChildren.push(...groupWithPartner(child)));
    sortMemberTypeByAgeDescending(getSinglePartnerChildren()).forEach(child => orderedChildren.push(...groupWithPartner(child)));
    return orderedChildren;
}

function hasParentChildRelationship(parent: MemberType, child: MemberType): boolean {
    const parentHasChild = parent.family?.some(rel => rel.type === FamilyRelationshipType.CHILD && rel.fromMember?.id === child.id);
    const childHasParent = child.family?.some(rel => rel.type === FamilyRelationshipType.PARENT && rel.fromMember?.id === parent.id);
    return Boolean(parentHasChild || childHasParent);
}

function getVerifiedChildren(member: MemberType, partner?: MemberType): MemberType[] {
    const candidates = getUniqueSortedChildren(member, partner);
    return candidates.filter(child =>
        hasParentChildRelationship(member, child) || (partner ? hasParentChildRelationship(partner, child) : false)
    );
}

function filterVerifiedChildrenForBlockedParents(
    member: MemberType,
    partner: MemberType | undefined,
    blockChildrenFromIds?: Set<string>
): MemberType[] {
    const children = getVerifiedChildren(member, partner);
    if (!blockChildrenFromIds || blockChildrenFromIds.size === 0) return children;
    const memberBlocked = blockChildrenFromIds.has(member.id);
    const partnerBlocked = partner ? blockChildrenFromIds.has(partner.id) : false;
    if (!partner) return memberBlocked ? [] : children;
    if (memberBlocked && partnerBlocked) return [];
    if (memberBlocked) return children.filter(child => hasParentChildRelationship(partner, child));
    if (partnerBlocked) return children.filter(child => hasParentChildRelationship(member, child));
    return children;
}

type ChildBlockContext = {
    blockedIds?: Set<string>;
    blockChildrenFromIds?: Set<string>;
};

type CollectFamilyTreeNodeOptions = {
    familyMode?: FamilyMode;
    visited?: Set<string>;
    x?: number;
    y?: number;
    context?: ChildBlockContext;
};

type FamilyTreeEdgeData = {
    anchor?: { x: number; y: number };
    joint?: boolean;
    parents?: string[];
    partnerAnchor?: {
        source: { x: number; y: number };
        target: { x: number; y: number };
    };
    verticalDrop?: number;
};

function collectFamilyTreeNodes(
    member: MemberType,
    nodes: Node[],
    onClick?: (member: MemberType, familyMode: FamilyMode) => void,
    options: CollectFamilyTreeNodeOptions = {}
) {
    const {
        familyMode = FamilyMode.IMMEDIATE,
        visited = new Set<string>(),
        x = 0,
        y = 0,
        context
    } = options;
    const blockedIds = context?.blockedIds;
    const blockChildrenFromIds = context?.blockChildrenFromIds;
    if (!member.id || visited.has(member.id)) return;
    if (blockedIds?.has(member.id)) return;
    visited.add(member.id);
    addFamilyMemberNode(member, nodes, onClick, familyMode, x, y);
    const partners = getPartnerRelationships(member);
    if (partners.length > 0) {
        partners.forEach((partner, idx) => {
            addFamilyMemberNode(partner, nodes, onClick, familyMode, x + (idx + 1) * HORIZONTAL_EDGE_OFFSET, y);
            const children = filterVerifiedChildrenForBlockedParents(member, partner, blockChildrenFromIds);
            children.forEach((child, cidx) => {
                collectFamilyTreeNodes(child, nodes, onClick, {
                    familyMode,
                    visited,
                    x: x + cidx * HORIZONTAL_EDGE_OFFSET,
                    y: y + 150,
                    context
                });
            });
        });
    } else {
        const children = filterVerifiedChildrenForBlockedParents(member, undefined, blockChildrenFromIds);
        children.forEach((child, cidx) => {
            collectFamilyTreeNodes(child, nodes, onClick, {
                familyMode,
                visited,
                x: x + cidx * HORIZONTAL_EDGE_OFFSET,
                y: y + 150,
                context
            });
        });
    }
}

function addEdge(
    edges: Edge[],
    source: string,
    target: string,
    type = "familyTree",
    data?: FamilyTreeEdgeData,
    sourceHandle = "bottom",
    targetHandle = "top"
) {
    const isPartnerEdge = sourceHandle === "right" && targetHandle === "left";
    const isReversePartnerEdge = sourceHandle === "left" && targetHandle === "right";
    const hasReversePartner = isPartnerEdge
        && edges.some(e => e.source === target && e.target === source && e.sourceHandle === "right" && e.targetHandle === "left");
    const hasReversePartnerAlt = isReversePartnerEdge
        && edges.some(e => e.source === target && e.target === source && e.sourceHandle === "left" && e.targetHandle === "right");

    if (!edges.some(e => e.source === source && e.target === target) && !hasReversePartner && !hasReversePartnerAlt) {
        edges.push({id: `${source}-${target}`, source, target, type, data, sourceHandle, targetHandle});
    }
}

function getSingleParentEdgeData(verticalDrop: number): FamilyTreeEdgeData {
    return {verticalDrop};
}

function collectFamilyTreeEdges(
    member: MemberType,
    edges: Edge[],
    visited: Set<string> = new Set(),
    context?: ChildBlockContext
) {
    const blockedIds = context?.blockedIds;
    const blockChildrenFromIds = context?.blockChildrenFromIds;
    if (!member.id || visited.has(member.id)) return;
    if (blockedIds?.has(member.id)) return;
    visited.add(member.id);
    const partners = getPartnerRelationships(member);
    if (partners.length > 0) {
        partners.forEach(partner => {
            const allowMemberChildren = !blockChildrenFromIds?.has(member.id);
            const allowPartnerChildren = !blockChildrenFromIds?.has(partner.id);
            const memberChildren = allowMemberChildren ? getChildRelationships(member) : [];
            const partnerChildren = allowPartnerChildren ? getChildRelationships(partner) : [];
            const partnerChildIds = new Set(partnerChildren.map((rel: FamilyRelationship) => rel.fromMember.id));
            const memberChildIds = new Set(memberChildren.map((rel: FamilyRelationship) => rel.fromMember.id));
            const jointChildren = memberChildren
                .filter((rel: FamilyRelationship) => partnerChildIds.has(rel.fromMember.id))
                .map((rel: FamilyRelationship) => rel.fromMember);
            const singleMemberChildren = memberChildren
                .filter((rel: FamilyRelationship) => !partnerChildIds.has(rel.fromMember.id))
                .map((rel: FamilyRelationship) => rel.fromMember);
            const singlePartnerChildren = partnerChildren
                .filter((rel: FamilyRelationship) => !memberChildIds.has(rel.fromMember.id))
                .map((rel: FamilyRelationship) => rel.fromMember);
            const jointChildIds = new Set(jointChildren.map((child: MemberType) => child.id));
            const singleMemberChildIds = new Set(singleMemberChildren.map((child: MemberType) => child.id));
            const singlePartnerChildIds = new Set(singlePartnerChildren.map((child: MemberType) => child.id));
            const shouldSeparateSingleParentEdges =
                singleMemberChildIds.size > 0
                && singlePartnerChildIds.size > 0
                && jointChildren.length === 0;
            const primaryParentId = [member.id, partner.id].sort((a, b) => a.localeCompare(b))[0];
            const hasBlockedJointChild = jointChildren.some(child => blockedIds?.has(child.id));

            if (partner && !hasBlockedJointChild) addEdge(edges, member.id, partner.id, "familyTree", undefined, "right", "left");

            jointChildren.forEach((child: MemberType) => {
                if (blockedIds?.has(child.id)) return;
                addEdge(edges, primaryParentId, child.id, "familyTree", {
                    joint: true,
                    parents: [member.id, partner.id].sort((a, b) => a.localeCompare(b))
                });
            });

            const children = filterVerifiedChildrenForBlockedParents(member, partner, blockChildrenFromIds);
            children.forEach(child => {
                if (blockedIds?.has(child.id)) return;
                if (partner && jointChildIds.has(child.id)) {
                    collectFamilyTreeEdges(child, edges, visited, context);
                    return;
                }
                if (!partner || memberChildIds.has(child.id) || hasParentChildRelationship(member, child)) {
                    addEdge(
                        edges,
                        member.id,
                        child.id,
                        "familyTree",
                        shouldSeparateSingleParentEdges && singleMemberChildIds.has(child.id)
                            ? getSingleParentEdgeData(SINGLE_PARENT_MEMBER_VERTICAL_DROP)
                            : undefined
                    );
                }
                collectFamilyTreeEdges(child, edges, visited, context);
            });
            if (partner) {
                singlePartnerChildren.forEach((child: MemberType) => {
                    if (blockedIds?.has(child.id)) return;
                    addEdge(
                        edges,
                        partner.id,
                        child.id,
                        "familyTree",
                        shouldSeparateSingleParentEdges && singlePartnerChildIds.has(child.id)
                            ? getSingleParentEdgeData(SINGLE_PARENT_PARTNER_VERTICAL_DROP)
                            : undefined
                    );
                    collectFamilyTreeEdges(child, edges, visited, context);
                });
            }
        });
    } else {
        const children = filterVerifiedChildrenForBlockedParents(member, undefined, blockChildrenFromIds);
        children.forEach(child => {
            if (blockedIds?.has(child.id)) return;
            addEdge(edges, member.id, child.id);
            collectFamilyTreeEdges(child, edges, visited, context);
        });
    }
}

function buildParentAndPartnerMaps(edges: Edge[]) {
    const parentMap = new Map<string, string[]>();
    const partnerMap = new Map<string, string>();
    const partnerRightOf = new Map<string, string>();
    edges.forEach(edge => {
        if (edge.type !== 'familyTree') return;
        if (edge.sourceHandle === 'right' && edge.targetHandle === 'left') {
            partnerMap.set(edge.source, edge.target);
            partnerMap.set(edge.target, edge.source);
            partnerRightOf.set(edge.source, edge.target);
            return;
        }
        if (edge.sourceHandle === 'bottom' && edge.targetHandle === 'top') {
            const jointParents = edge.data?.parents as string[] | undefined;
            if (jointParents && jointParents.length > 0) {
                parentMap.set(edge.target, jointParents);
            } else {
                const existing = parentMap.get(edge.target) ?? [];
                if (!existing.includes(edge.source)) {
                    parentMap.set(edge.target, [...existing, edge.source]);
                }
            }
        }
    });
    return {parentMap, partnerMap, partnerRightOf};
}

function buildParentGroups(
    nodesAtLevel: Node[],
    level: number,
    parentMap: Map<string, string[]>,
    nodes: Node[]
) {
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const parentGroups = new Map<string, Node[]>();
    parentMap.forEach((parents, childId) => {
        const childNode = nodeById.get(childId);
        if (!childNode) return;
        if (Math.round((childNode.position.y ?? 0) / VERTICAL_NODE_GAP) !== level + 1) return;
        const groupNodes = parents
            .map(parentId => nodesAtLevel.find(n => n.id === parentId))
            .filter(Boolean) as Node[];
        if (groupNodes.length === 0) return;
        const groupKey = parents.slice().sort((a, b) => a.localeCompare(b)).join(':');
        parentGroups.set(groupKey, groupNodes);
    });
    return parentGroups;
}

type ExtendedLayoutContext = {
    levelMap: Map<number, Node[]>;
    parentMap: Map<string, string[]>;
    partnerMap: Map<string, string>;
    nodes: Node[];
    primarySideParentIds: Set<string>;
    partnerSideParentIds: Set<string>;
};

function buildGroupKey(ids: string[]) {
    return ids.slice().sort((a, b) => a.localeCompare(b)).join(':');
}

function addNodeIfMissing(groupNodes: Node[], seen: Set<string>, node?: Node) {
    if (!node || seen.has(node.id)) return;
    groupNodes.push(node);
    seen.add(node.id);
}

function addPartnerPair(
    groupNodes: Node[],
    seen: Set<string>,
    parent: Node,
    partnerNode: Node,
    preferPartnerFirst: boolean
) {
    if (preferPartnerFirst) {
        addNodeIfMissing(groupNodes, seen, partnerNode);
        addNodeIfMissing(groupNodes, seen, parent);
        return;
    }
    addNodeIfMissing(groupNodes, seen, parent);
    addNodeIfMissing(groupNodes, seen, partnerNode);
}

function buildExtendedGroupNodes(
    parents: Node[],
    nodesAtLevel: Node[],
    partnerMap: Map<string, string>
): Node[] {
    const sortedParents = parents.slice().sort((a, b) => a.id.localeCompare(b.id));
    const groupNodes: Node[] = [];
    const seen = new Set<string>();

    if (sortedParents.length > 1) {
        const midpoint = sortedParents.length / 2;
        sortedParents.forEach((parent, idx) => {
            const partnerId = partnerMap.get(parent.id);
            const partnerNode = partnerId ? nodesAtLevel.find(n => n.id === partnerId) : undefined;
            const partnerIsParent = partnerNode ? sortedParents.some(p => p.id === partnerNode.id) : false;
            if (partnerNode && !partnerIsParent) {
                addPartnerPair(groupNodes, seen, parent, partnerNode, idx < midpoint);
                return;
            }
            addNodeIfMissing(groupNodes, seen, parent);
        });
        return groupNodes;
    }

    sortedParents.forEach(parent => {
        addNodeIfMissing(groupNodes, seen, parent);
    });
    return groupNodes;
}

function getChildIdForGroupKey(parentMap: Map<string, string[]>, groupKey: string) {
    let childId: string | undefined;
    parentMap.forEach((ids, id) => {
        if (childId) return;
        if (buildGroupKey(ids) === groupKey) childId = id;
    });
    return childId;
}

function getChildrenForParent(parentMap: Map<string, string[]>, nodes: Node[], parentId: string) {
    const children: Node[] = [];
    parentMap.forEach((parentIds, childId) => {
        if (!parentIds.includes(parentId)) return;
        const child = nodes.find(n => n.id === childId);
        if (child) children.push(child);
    });
    return children;
}

function placeGroupRow(
    groupNodes: Node[],
    startX: number,
    level: number,
    positioned: Set<string>
) {
    let x = startX;
    groupNodes.forEach(node => {
        const width = getNodeWidth(node);
        node.position.x = x + width / 2;
        node.position.y = level * VERTICAL_NODE_GAP;
        positioned.add(node.id);
        x += width + HORIZONTAL_NODE_GAP;
    });
    return x;
}

function placeNodesRow(nodes: Node[], nodeWidths: number[], startX: number, y: number) {
    let x = startX;
    for (let i = 0; i < nodes.length; i++) {
        nodes[i].position.x = x + nodeWidths[i] / 2;
        nodes[i].position.y = y;
        x += nodeWidths[i] + HORIZONTAL_NODE_GAP;
    }
}

function getRowMetrics(nodes: Node[]) {
    const widths = nodes.map(getNodeWidth);
    const totalWidth = widths.reduce((sum, w) => sum + w, 0)
        + Math.max(0, widths.length - 1) * HORIZONTAL_NODE_GAP;
    return {widths, totalWidth};
}

function layoutSupplementalRootNodes(
    levelMap: Map<number, Node[]>,
    mainMemberIds: string[],
    showExtendedFamily: boolean,
    parentMap: Map<string, string[]>,
    primarySideParentIds: Set<string>,
    partnerSideParentIds: Set<string>,
    partnerMap: Map<string, string>
) {
    if (!showExtendedFamily || !levelMap.has(0)) return;

    const rootNodes = levelMap.get(0)!;
    const mainIdSet = new Set(mainMemberIds);
    const mainNodes = rootNodes.filter(node => mainIdSet.has(node.id));
    const extraNodes = rootNodes.filter(node => !mainIdSet.has(node.id));
    if (mainNodes.length === 0 || extraNodes.length === 0) return;

    const isOnSide = (parentIds: string[], sideIds: Set<string>) => parentIds.some(parentId => {
        if (sideIds.has(parentId)) return true;
        const partnerId = partnerMap.get(parentId);
        return typeof partnerId === "string" && sideIds.has(partnerId);
    });

    const leftNodes = extraNodes.filter(node => {
        const parents = parentMap.get(node.id) ?? [];
        return isOnSide(parents, primarySideParentIds);
    });
    const rightNodes = extraNodes.filter(node => {
        const parents = parentMap.get(node.id) ?? [];
        return isOnSide(parents, partnerSideParentIds);
    });
    const unassigned = extraNodes.filter(node => !leftNodes.includes(node) && !rightNodes.includes(node));
    leftNodes.push(...unassigned.slice(0, Math.ceil(unassigned.length / 2)));
    rightNodes.push(...unassigned.slice(Math.ceil(unassigned.length / 2)));

    leftNodes.sort((left, right) => (left.position.x ?? 0) - (right.position.x ?? 0));
    rightNodes.sort((left, right) => (left.position.x ?? 0) - (right.position.x ?? 0));

    const mainLeft = Math.min(...mainNodes.map(node => (node.position.x ?? 0) - getNodeWidth(node) / 2));
    const mainRight = Math.max(...mainNodes.map(node => (node.position.x ?? 0) + getNodeWidth(node) / 2));
    const leftWidths = leftNodes.map(getNodeWidth);
    const rightWidths = rightNodes.map(getNodeWidth);
    const leftTotal = leftWidths.reduce((sum, width) => sum + width, 0) + Math.max(0, leftNodes.length - 1) * HORIZONTAL_NODE_GAP;

    if (leftNodes.length > 0) {
        const leftX = mainLeft - HORIZONTAL_NODE_GAP - leftTotal;
        placeNodesRow(leftNodes, leftWidths, leftX, 0);
    }

    if (rightNodes.length > 0) {
        const rightX = mainRight + HORIZONTAL_NODE_GAP;
        placeNodesRow(rightNodes, rightWidths, rightX, 0);
    }
}

function layoutSupplementalPositiveLevels(
    levelMap: Map<number, Node[]>,
    parentMap: Map<string, string[]>,
    partnerMap: Map<string, string>,
    nodes: Node[],
    mainTreeNodeIds: Set<string>
) {
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const levels = Array.from(levelMap.keys()).sort((left, right) => left - right);

    for (const level of levels) {
        if (level <= 0) continue;

        const nodesAtLevel = (levelMap.get(level) ?? []).filter(node => !mainTreeNodeIds.has(node.id));
        if (nodesAtLevel.length === 0) continue;

        const orderedAtLevel = nodesAtLevel.slice().sort((left, right) => (left.position.x ?? 0) - (right.position.x ?? 0));
        const orderIndex = new Map(orderedAtLevel.map((node, index) => [node.id, index]));
        const parentGroups = new Map<string, Node[]>();

        nodesAtLevel.forEach(node => {
            const parents = parentMap.get(node.id);
            if (!parents || parents.length === 0) return;
            const key = parents.slice().sort((left, right) => left.localeCompare(right)).join(":");
            if (!parentGroups.has(key)) parentGroups.set(key, []);
            parentGroups.get(key)!.push(node);
        });

        parentGroups.forEach((children, parentKey) => {
            children.sort((left, right) => (orderIndex.get(left.id) ?? 0) - (orderIndex.get(right.id) ?? 0));
            const {widths: nodeWidths, totalWidth} = getRowMetrics(children);
            const parentNodes = parentKey.split(":").map(id => nodeById.get(id)).filter(Boolean) as Node[];
            if (parentNodes.length === 0) return;
            const parentX = getCenterXFromNodes(parentNodes);
            placeNodesRow(children, nodeWidths, parentX - totalWidth / 2, level * VERTICAL_NODE_GAP);
        });

        const processedPairs = new Set<string>();
        nodesAtLevel.forEach(node => {
            const partnerId = partnerMap.get(node.id);
            if (!partnerId) return;
            const partnerNode = nodesAtLevel.find(candidate => candidate.id === partnerId);
            if (!partnerNode) return;

            const nodeHasParents = (parentMap.get(node.id)?.length ?? 0) > 0;
            const partnerHasParents = (parentMap.get(partnerId)?.length ?? 0) > 0;
            if (nodeHasParents === partnerHasParents) return;

            const childNode = nodeHasParents ? node : partnerNode;
            const spouseNode = nodeHasParents ? partnerNode : node;
            const key = `${childNode.id}:${spouseNode.id}`;
            if (processedPairs.has(key)) return;
            processedPairs.add(key);

            const childCenter = childNode.position.x ?? 0;
            const childWidth = getNodeWidth(childNode);
            const spouseWidth = getNodeWidth(spouseNode);
            const pairWidth = childWidth + HORIZONTAL_NODE_GAP + spouseWidth;
            const leftEdge = childCenter - pairWidth / 2;
            childNode.position.x = leftEdge + childWidth / 2;
            spouseNode.position.x = leftEdge + childWidth + HORIZONTAL_NODE_GAP + spouseWidth / 2;
            spouseNode.position.y = childNode.position.y;
        });
    }
}

function placeGroupsBySide(
    ctx: ExtendedLayoutContext,
    parentGroups: Map<string, Node[]>,
    nodesAtLevel: Node[],
    level: number,
    positioned: Set<string>,
    mainCenter: number
) {
    const groupEntries = Array.from(parentGroups.entries()).map(([groupKey, parents]) => {
        const groupNodes = buildExtendedGroupNodes(parents, nodesAtLevel, ctx.partnerMap);
        const totalWidth = groupNodes.reduce((sum, node) => sum + getNodeWidth(node), 0)
            + Math.max(0, groupNodes.length - 1) * HORIZONTAL_NODE_GAP;
        const parentIds = groupKey.split(':');
        const isPartnerSide = parentIds.some(id => ctx.partnerSideParentIds.has(id));
        return {groupNodes, totalWidth, side: isPartnerSide ? 'right' : 'left'};
    });

    const leftGroups = groupEntries.filter(entry => entry.side === 'left');
    const rightGroups = groupEntries.filter(entry => entry.side === 'right');
    const leftTotal = leftGroups.reduce((sum, entry) => sum + entry.totalWidth, 0)
        + Math.max(0, leftGroups.length - 1) * HORIZONTAL_NODE_GAP;
    const sideGap = HORIZONTAL_NODE_GAP;

    let leftX = mainCenter - sideGap / 2 - leftTotal;
    leftGroups.forEach(entry => {
        leftX = placeGroupRow(entry.groupNodes, leftX, level, positioned);
    });

    let rightX = mainCenter + sideGap / 2;
    rightGroups.forEach(entry => {
        rightX = placeGroupRow(entry.groupNodes, rightX, level, positioned);
    });
}

function placeGroupsUnderChildren(
    ctx: ExtendedLayoutContext,
    parentGroups: Map<string, Node[]>,
    level: number,
    positioned: Set<string>
) {
    parentGroups.forEach((parents, groupKey) => {
        const groupNodes = buildExtendedGroupNodes(parents, ctx.levelMap.get(level) ?? [], ctx.partnerMap);
        groupNodes.forEach(parent => positioned.add(parent.id));
        const parentWidths = groupNodes.map(getNodeWidth);
        const totalWidth = parentWidths.reduce((sum, w) => sum + w, 0) + (groupNodes.length - 1) * HORIZONTAL_NODE_GAP;
        const childId = getChildIdForGroupKey(ctx.parentMap, groupKey);
        const child = childId ? ctx.nodes.find(n => n.id === childId) : undefined;
        const centerX = child ? child.position.x : 0;
        let x = centerX - totalWidth / 2;
        for (let i = 0; i < groupNodes.length; i++) {
            groupNodes[i].position.x = x + parentWidths[i] / 2;
            groupNodes[i].position.y = level * VERTICAL_NODE_GAP;
            x += parentWidths[i] + HORIZONTAL_NODE_GAP;
        }
    });
}

function placeUnpositionedNodes(
    ctx: ExtendedLayoutContext,
    nodesAtLevel: Node[],
    level: number,
    positioned: Set<string>
) {
    nodesAtLevel.forEach(node => {
        if (positioned.has(node.id)) return;
        const partnerId = ctx.partnerMap.get(node.id);
        const partnerNode = partnerId ? nodesAtLevel.find(n => n.id === partnerId) : undefined;
        const children = getChildrenForParent(ctx.parentMap, ctx.nodes, node.id);
        const childrenCenter = children.length > 0
            ? children.map(n => n.position.x).reduce((a, b) => a + b, 0) / children.length
            : 0;
        if (partnerNode && !positioned.has(partnerNode.id)) {
            const pairWidths = [getNodeWidth(node), getNodeWidth(partnerNode)];
            const totalWidth = pairWidths.reduce((sum, w) => sum + w, 0) + HORIZONTAL_NODE_GAP;
            let x = childrenCenter - totalWidth / 2;
            node.position.x = x + pairWidths[0] / 2;
            node.position.y = level * VERTICAL_NODE_GAP;
            x += pairWidths[0] + HORIZONTAL_NODE_GAP;
            partnerNode.position.x = x + pairWidths[1] / 2;
            partnerNode.position.y = level * VERTICAL_NODE_GAP;
            positioned.add(node.id);
            positioned.add(partnerNode.id);
            return;
        }
        if (partnerNode && positioned.has(partnerNode.id)) {
            const partnerX = partnerNode.position.x ?? 0;
            const partnerWidth = getNodeWidth(partnerNode);
            const nodeWidth = getNodeWidth(node);
            const placeRight = childrenCenter >= partnerX;
            node.position.x = placeRight
                ? partnerX + partnerWidth / 2 + HORIZONTAL_NODE_GAP + nodeWidth / 2
                : partnerX - partnerWidth / 2 - HORIZONTAL_NODE_GAP - nodeWidth / 2;
            node.position.y = level * VERTICAL_NODE_GAP;
            positioned.add(node.id);
            return;
        }
        node.position.x = childrenCenter;
        node.position.y = level * VERTICAL_NODE_GAP;
        positioned.add(node.id);
    });
}

function layoutExtendedLevels(
    layout: {
        levels: number[];
        levelMap: Map<number, Node[]>;
        parentMap: Map<string, string[]>;
        partnerMap: Map<string, string>;
        nodes: Node[];
        mainMemberIds: string[];
        primarySideParentIds: Set<string>;
        partnerSideParentIds: Set<string>;
    }
) {
    const {
        levels,
        levelMap,
        parentMap,
        partnerMap,
        nodes,
        mainMemberIds,
        primarySideParentIds,
        partnerSideParentIds
    } = layout;
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const mainNodes = mainMemberIds
        .map(id => nodeById.get(id))
        .filter(Boolean) as Node[];
    const mainCenter = mainNodes.length > 0
        ? mainNodes.map(n => n.position.x ?? 0).reduce((a, b) => a + b, 0) / mainNodes.length
        : 0;
    const minLevel = Math.min(...levels);
    const ctx: ExtendedLayoutContext = {
        levelMap,
        parentMap,
        partnerMap,
        nodes,
        primarySideParentIds,
        partnerSideParentIds
    };

    for (let level = -1; level >= minLevel; level--) {
        const nodesAtLevel = levelMap.get(level);
        if (!nodesAtLevel) continue;
        const positioned = new Set<string>();
        const parentGroups = buildParentGroups(nodesAtLevel, level, parentMap, nodes);

        if (level === -1 && (primarySideParentIds.size > 0 || partnerSideParentIds.size > 0)) {
            placeGroupsBySide(ctx, parentGroups, nodesAtLevel, level, positioned, mainCenter);
        } else {
            placeGroupsUnderChildren(ctx, parentGroups, level, positioned);
        }

        placeUnpositionedNodes(ctx, nodesAtLevel, level, positioned);
    }
}

type LayoutPhaseContext = {
    levelMap: Map<number, Node[]>;
    parentMap: Map<string, string[]>;
    partnerMap: Map<string, string>;
    partnerRightOf: Map<string, string>;
    levels: number[];
    nodes: Node[];
    showExtendedFamily: boolean;
    mainMemberIds: string[];
    primarySideParentIds: Set<string>;
    partnerSideParentIds: Set<string>;
    mainTreeNodeIds: Set<string>;
};

type MainTreeLayoutContext = Pick<
    LayoutPhaseContext,
    'levelMap' | 'parentMap' | 'partnerMap' | 'partnerRightOf' | 'nodes' | 'mainMemberIds' | 'showExtendedFamily' | 'primarySideParentIds' | 'partnerSideParentIds' | 'mainTreeNodeIds'
>;

function applyMainTreeLayoutPhase(ctx: MainTreeLayoutContext) {
    ctx.mainTreeNodeIds = applyMainTreeTidyLayout({
        nodes: ctx.nodes,
        parentMap: ctx.parentMap,
        partnerMap: ctx.partnerMap,
        partnerRightOf: ctx.partnerRightOf,
        mainMemberIds: ctx.mainMemberIds
    });
    layoutSupplementalRootNodes(
        ctx.levelMap,
        ctx.mainMemberIds,
        ctx.showExtendedFamily,
        ctx.parentMap,
        ctx.primarySideParentIds,
        ctx.partnerSideParentIds,
        ctx.partnerMap
    );
    layoutSupplementalPositiveLevels(
        ctx.levelMap,
        ctx.parentMap,
        ctx.partnerMap,
        ctx.nodes,
        ctx.mainTreeNodeIds
    );
}

function applyInitialLayoutPhase(ctx: LayoutPhaseContext) {
    applyMainTreeLayoutPhase(ctx);

    if (ctx.showExtendedFamily) {
        layoutExtendedLevels({
            levels: ctx.levels,
            levelMap: ctx.levelMap,
            parentMap: ctx.parentMap,
            partnerMap: ctx.partnerMap,
            nodes: ctx.nodes,
            mainMemberIds: ctx.mainMemberIds,
            primarySideParentIds: ctx.primarySideParentIds,
            partnerSideParentIds: ctx.partnerSideParentIds
        });
    }
}

function applySpacingPhase(ctx: LayoutPhaseContext) {
    applyCenteredTidySpacing({
        levelMap: ctx.levelMap,
        parentMap: ctx.parentMap,
        partnerRightOf: ctx.partnerRightOf,
        nodes: ctx.nodes,
        mainTreeNodeIds: ctx.mainTreeNodeIds,
        mainMemberIds: ctx.mainMemberIds
    });

    if (ctx.showExtendedFamily) {
        centerExtendedFamilyAboveMain(ctx.nodes, ctx.mainMemberIds);
        const hasPrimarySide = ctx.primarySideParentIds.size > 0;
        const hasPartnerSide = ctx.partnerSideParentIds.size > 0;
        if (hasPrimarySide && hasPartnerSide) {
            enforceExtendedFamilySides(ctx.nodes, ctx.primarySideParentIds, ctx.partnerSideParentIds, ctx.partnerMap);
        }
    }
}

function applyAlignmentPhase(ctx: LayoutPhaseContext) {
    if (ctx.showExtendedFamily) {
        centerExtendedFamilyAboveMain(ctx.nodes, ctx.mainMemberIds);
    }

    enforcePartnerMaxGap(ctx.levelMap, ctx.partnerMap);
}

function applyFamilyTreeCustomLayout(
    nodes: Node[],
    edges: Edge[],
    showExtendedFamily: boolean = false,
    mainMemberIds: string[] = [],
    primarySideParentIds: Set<string> = new Set(),
    partnerSideParentIds: Set<string> = new Set()
): Node[] {
    const {parentMap, partnerMap, partnerRightOf} = buildParentAndPartnerMaps(edges);
    const levelMap = buildLevelMap(nodes);
    const levels = Array.from(levelMap.keys()).sort((a, b) => a - b);

    const layoutContext: LayoutPhaseContext = {
        levelMap,
        parentMap,
        partnerMap,
        partnerRightOf,
        levels,
        nodes,
        showExtendedFamily,
        mainMemberIds,
        primarySideParentIds,
        partnerSideParentIds,
        mainTreeNodeIds: new Set()
    };

    applyInitialLayoutPhase(layoutContext);
    applySpacingPhase(layoutContext);
    applyAlignmentPhase(layoutContext);

    return nodes;
}

function cloneNodesForLayout(nodes: Node[]): Node[] {
    return nodes.map(node => ({
        ...node,
        position: {
            x: node.position?.x ?? 0,
            y: node.position?.y ?? 0
        }
    }));
}

function getLaidOutNodes(
    nodes: Node[],
    edges: Edge[],
    showExtendedFamily: boolean = false,
    mainMemberIds: string[] = [],
    primarySideParentIds: Set<string> = new Set(),
    partnerSideParentIds: Set<string> = new Set()
): Node[] {
    if (!nodes || nodes.length === 0) return [];
    const layoutNodes = cloneNodesForLayout(nodes);
    return applyFamilyTreeCustomLayout(layoutNodes, edges, showExtendedFamily, mainMemberIds, primarySideParentIds, partnerSideParentIds);
}

function getNodeIdsSet(nodes: Node[]) {
    return new Set(nodes.map(node => node.id));
}

function getPartnerRelationships(member: MemberType): MemberType[] {
    return findPartners(member);
}

function getFirstPartner(member: MemberType): MemberType | null {
    return getPartnerRelationships(member)[0] || null;
}

function arePartners(member: MemberType, partner: MemberType): boolean {
    return getPartnerRelationships(member).some(p => p.id === partner.id);
}

function getSortedParents(member: MemberType): MemberType[] {
    return sortMemberTypeByAgeDescending(findExtendedParents(member) ?? []);
}

function getOtherMembersSectionNode(
    familyTree: FamilyTreeType,
    nodes: Node[],
    onClick?: (member: MemberType, familyMode: FamilyMode) => void,
    showOtherMembers: boolean = true,
    showAddOtherMembersButton: boolean = true,
    onAddOtherMembersClick?: () => void,
    isProfileWithProposalsOrArchived: boolean = false
): Node | null {
    const hasOtherMembers = Array.isArray(familyTree.primaryContact.otherMembers) && familyTree.primaryContact.otherMembers.length > 0;
    const showOtherMembersSection = showOtherMembers && (hasOtherMembers || !isProfileWithProposalsOrArchived);
    if (!showOtherMembersSection) return null;
    const otherMembersArr = Array.isArray(familyTree.primaryContact.otherMembers)
        ? familyTree.primaryContact.otherMembers
        : [];
    const otherMemberTypes = otherMembersArr
        .map(rel => rel.fromMember)
        .filter((member): member is MemberType => !!member && typeof member.id === 'string');
    const sortedOtherMemberTypes = sortMemberTypeByAgeDescending(otherMemberTypes);
    const otherMembers = sortedOtherMemberTypes
        .map(member => ({
            id: member.id,
            firstName: member.firstName,
            lastName: member.lastName,
            age: member.age,
            state: member.stateAbbr ?? undefined,
            onClick: onClick ? () => onClick(member, FamilyMode.OTHER) : undefined,
            style: getBaseNodeStyle(member),
        }));
    let maxY = 0;
    if (nodes.length > 0) {
        maxY = Math.max(...nodes.map(n => n.position.y ?? 0));
    }
    const nodeWidths = sortedOtherMemberTypes.length > 0 ? sortedOtherMemberTypes.map(getNodeWidth) : [NODE_WIDTH_DEFAULT];
    const totalWidth = nodeWidths.reduce((sum, w) => sum + w, 0) + (nodeWidths.length - 1) * HORIZONTAL_NODE_GAP;
    const hasMembers = sortedOtherMemberTypes.length > 0;
    return {
        id: 'other-members-section',
        type: 'otherMembersSection',
        data: {
            otherMembers,
            onAddOtherMembersClick: showAddOtherMembersButton && typeof onAddOtherMembersClick === 'function' ? onAddOtherMembersClick : undefined,
            showAddOtherMembersButton
        },
        position: {x: -totalWidth / 2 + nodeWidths[0] / 2, y: maxY + OTHER_MEMBERS_MARGIN_TOP},
        width: totalWidth,
        height: hasMembers ? OTHER_MEMBERS_SECTION_HEIGHT_WITH_MEMBERS : OTHER_MEMBERS_SECTION_HEIGHT_EMPTY
    };
}

// --- Simplified Extended Family Helpers ---

/**
 * Returns direct parents and their partners for the primary contact.
 */
function getPrimarySideExtendedFamily(primary: MemberType): MemberType[] {
    const parents = getSortedParents(primary);
    return parents.flatMap(parent => [parent, getFirstPartner(parent)].filter(Boolean) as MemberType[]);
}

/**
 * Returns direct parents and their partners for all partners.
 */
function getPartnerSideExtendedFamily(partners: MemberType[]): MemberType[] {
    return partners.flatMap(partner => {
        const parents = getSortedParents(partner);
        return parents.flatMap(parent => [parent, getFirstPartner(parent)].filter(Boolean) as MemberType[]);
    });
}

/**
 * Deduplicates an array of MemberType by id.
 */
function dedupeMembers(members: MemberType[]): MemberType[] {
    const seen = new Set<string>();
    return members.filter(m => {
        if (!m || seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
    });
}

/**
 * Adds extended family nodes and edges to the layout, handling split layout and deduplication.
 */
function addExtendedFamilyNodesAndEdges(
    familyTree: FamilyTreeType,
    edges: Edge[],
    showExtendedFamily: boolean,
    partners: MemberType[]
) {
    if (!showExtendedFamily) return;
    const primaryParents = getSortedParents(familyTree.primaryContact);
    addPrimaryAndPartnerEdges(
        familyTree,
        edges,
        primaryParents,
        partners
    );
}

function applyJointAnchors(edges: Edge[], laidOutNodes: Node[]): Edge[] {
    const nodeMap = new Map(laidOutNodes.map(n => [n.id, n]));
    const getNodeBottomY = (node: Node) => {
        const height = typeof node.height === 'number' && node.height > 0 ? node.height : FAMILY_MEMBER_HEIGHT;
        return node.position.y + height / 2;
    };
    const getNodeCenter = (node: Node) => {
        return {
            x: node.position.x,
            y: node.position.y
        };
    };

    return edges.map(edge => {
        const isPartnerEdge = edge.sourceHandle === "right" && edge.targetHandle === "left";
        if (isPartnerEdge) {
            const sourceNode = nodeMap.get(edge.source);
            const targetNode = nodeMap.get(edge.target);
            if (sourceNode && targetNode) {
                return {
                    ...edge,
                    data: {
                        ...edge.data,
                        partnerAnchor: {
                            source: getNodeCenter(sourceNode),
                            target: getNodeCenter(targetNode)
                        }
                    }
                };
            }
        }

        const parents = edge.data?.parents as string[] | undefined;
        if (!parents || parents.length < 2) return edge;
        const parentNodes = parents.map(id => nodeMap.get(id)).filter(Boolean) as Node[];
        if (parentNodes.length < 2) return edge;

        if (parentNodes.length === 2) {
            const [leftParent, rightParent] = parentNodes[0].position.x <= parentNodes[1].position.x
                ? [parentNodes[0], parentNodes[1]]
                : [parentNodes[1], parentNodes[0]];
            const leftHandleX = getNodeHandleX(leftParent, 'right');
            const rightHandleX = getNodeHandleX(rightParent, 'left');
            const leftHandleY = getNodeHandleY(leftParent);
            const rightHandleY = getNodeHandleY(rightParent);
            return {
                ...edge,
                data: {
                    ...edge.data,
                    anchor: {
                        x: (leftHandleX + rightHandleX) / 2,
                        y: (leftHandleY + rightHandleY) / 2
                    }
                }
            };
        }

        const anchorX = parentNodes.map(n => n.position.x).reduce((a, b) => a + b, 0) / parentNodes.length;
        const anchorY = parentNodes.map(getNodeBottomY).reduce((a, b) => a + b, 0) / parentNodes.length;
        return {
            ...edge,
            data: {
                ...edge.data,
                anchor: {x: anchorX, y: anchorY}
            }
        };
    });
}

/**
 * Adds edges for primary and partner extended family relationships.
 */
function addPrimaryAndPartnerEdges(
    familyTree: FamilyTreeType,
    edges: Edge[],
    primaryParents: MemberType[],
    partners: MemberType[]
) {
    const addParentPartners = (parentList: MemberType[]) => {
        parentList.forEach(parent => {
            getPartnerRelationships(parent).forEach(parentPartner => {
                if (!parentPartner?.id) return;
                addEdge(edges, parent.id, parentPartner.id, "familyTree", undefined, "right", "left");
            });
        });
    };

    const addParentsForChild = (childId: string, parents: MemberType[]) => {
        if (!parents || parents.length === 0) return;
        const usedInJoint = new Set<string>();
        parents.forEach(parent => {
            if (usedInJoint.has(parent.id)) return;
            const partnerInList = parents.find(other => other.id !== parent.id && !usedInJoint.has(other.id) && arePartners(parent, other));
            if (partnerInList) {
                const parentIds = [parent.id, partnerInList.id].sort((a, b) => a.localeCompare(b));
                const primaryParentId = parentIds[0];
                addEdge(edges, primaryParentId, childId, "familyTree", {
                    joint: true,
                    parents: parentIds
                }, "bottom", "top");
                usedInJoint.add(parent.id);
                usedInJoint.add(partnerInList.id);
            }
        });
        parents.forEach(parent => {
            if (!usedInJoint.has(parent.id)) {
                addEdge(edges, parent.id, childId, "familyTree", undefined, "bottom", "top");
            }
        });
    };

    addParentsForChild(familyTree.primaryContact.id, primaryParents);
    addParentPartners(primaryParents);

    partners.forEach(partner => {
        const partnerParents = getSortedParents(partner);
        addParentsForChild(partner.id, partnerParents);
        addParentPartners(partnerParents);
    });
}

export function generateFamilyTreeReactFlowData(
    familyTree: FamilyTreeType,
    onClick?: (member: MemberType, familyMode: FamilyMode) => void,
    showOtherMembers: boolean = true,
    showAddOtherMembersButton: boolean = true,
    onAddOtherMembersClick?: () => void,
    showExtendedFamily: boolean = false,
    isProfileWithProposalsOrArchived: boolean = false
): { nodes: Node[]; edges: Edge[] } {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    if (!familyTree.primaryContact) {
        return {nodes: [], edges: []};
    }
    collectFamilyTreeNodes(familyTree.primaryContact, nodes, onClick, {familyMode: FamilyMode.IMMEDIATE});
    collectFamilyTreeEdges(familyTree.primaryContact, edges);

    applyExSpouseStyles(nodes, familyTree.primaryContact);
    applyPrimaryAndPartnerStyles(nodes, familyTree.primaryContact);

    const otherMembersSectionNode = getOtherMembersSectionNode(
        familyTree,
        nodes,
        onClick,
        showOtherMembers,
        showAddOtherMembersButton,
        onAddOtherMembersClick,
        isProfileWithProposalsOrArchived
    );
    if (otherMembersSectionNode) {
        nodes.push(otherMembersSectionNode);
    }

    const partners = getPartnerRelationships(familyTree.primaryContact);
    const blockedExtendedIds = new Set([familyTree.primaryContact.id, ...partners.map(p => p.id)]);
    if (showExtendedFamily) {
        const extendedRoots = dedupeMembers([
            ...getPrimarySideExtendedFamily(familyTree.primaryContact),
            ...getPartnerSideExtendedFamily(partners)
        ]);
        const visitedExtended = new Set<string>();
        const context = {blockedIds: blockedExtendedIds};
        extendedRoots.forEach((root, idx) => {
            collectFamilyTreeNodes(
                root,
                nodes,
                onClick,
                {
                    familyMode: FamilyMode.EXTENDED,
                    visited: visitedExtended,
                    x: idx * HORIZONTAL_EDGE_OFFSET,
                    y: -VERTICAL_NODE_GAP,
                    context
                }
            );
            collectFamilyTreeEdges(root, edges, new Set(), context);
        });
    }

    addExtendedFamilyNodesAndEdges(familyTree, edges, showExtendedFamily, partners);

    const nodeIds = getNodeIdsSet(nodes);
    const validEdges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
    const primaryNode = nodes.find(n => n.id === familyTree.primaryContact.id);
    const partnerNodes = partners.map(p => nodes.find(n => n.id === p.id)).filter(Boolean) as Node[];
    const mainNodes = [primaryNode, ...partnerNodes].filter(Boolean) as Node[];
    const primarySideParentIds = new Set(getPrimarySideExtendedFamily(familyTree.primaryContact).map(m => m.id));
    const partnerSideParentIds = new Set(getPartnerSideExtendedFamily(partners).map(m => m.id));

    // Dev-only guard to catch accidental data mutations during layout.
    freezeNodeData(nodes);

    const laidOutNodes = getLaidOutNodes(
        nodes,
        validEdges,
        showExtendedFamily,
        mainNodes.map(n => n.id),
        primarySideParentIds,
        partnerSideParentIds
    );

    const mainNodeIds = new Set(mainNodes.map(n => n.id));
    const laidOutMainNodes = laidOutNodes.filter(node => mainNodeIds.has(node.id));
    const mainYs = laidOutMainNodes.map(node => node.position.y);
    const minY = mainYs.length > 0 ? Math.min(...mainYs) : 0;
    if (showExtendedFamily) {
        const extendedNodes = laidOutNodes.filter(node => node.position.y < minY && node.type === 'familyMember');
        extendedNodes.forEach(node => {
            node.position.y = minY - VERTICAL_NODE_GAP;
        });
    }

    updateOtherMembersSectionPosition(laidOutNodes);

    const finalNodeIds = getNodeIdsSet(laidOutNodes);
    let finalEdges = edges.filter(e => finalNodeIds.has(e.source) && finalNodeIds.has(e.target));
    finalEdges = applyJointAnchors(finalEdges, laidOutNodes);
    return {nodes: laidOutNodes, edges: finalEdges};
}

function updateOtherMembersSectionPosition(nodes: Node[]) {
    const otherMembersSection = nodes.find(node => node.type === 'otherMembersSection');
    if (!otherMembersSection) return;

    const familyNodes = nodes.filter(node => node.type === 'familyMember');
    if (familyNodes.length === 0) return;

    const maxFamilyBottom = Math.max(...familyNodes.map(node => {
        const height = typeof node.height === 'number' && node.height > 0 ? node.height : FAMILY_MEMBER_HEIGHT;
        return (node.position.y ?? 0) + height / 2;
    }));

    const familyLeft = Math.min(...familyNodes.map(node => (node.position.x ?? 0) - getNodeWidth(node) / 2));
    const familyRight = Math.max(...familyNodes.map(node => (node.position.x ?? 0) + getNodeWidth(node) / 2));
    const familyCenter = (familyLeft + familyRight) / 2;

    const sectionHeight = typeof otherMembersSection.height === 'number' && otherMembersSection.height > 0
        ? otherMembersSection.height
        : OTHER_MEMBERS_SECTION_HEIGHT_WITH_MEMBERS;

    otherMembersSection.position.x = familyCenter;
    otherMembersSection.position.y = maxFamilyBottom + OTHER_MEMBERS_MARGIN_TOP + sectionHeight / 2;
}

export const __test__ = {
    placeGroupsUnderChildren,
    placeUnpositionedNodes,
};
